import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import type { FormEvent } from "react";
import type { KundaliRequest } from "@/lib/api/kundali";
import type { CustomerToolKey } from "@ishvaram/domain-data/customer-tools";
import type { PlaceResult } from "@ishvaram/ui/hooks";
import { kundaliFormSchema, toKundaliRequest } from "@ishvaram/api-client/forms/kundali";
import { trackFreeToolStarted } from "@/lib/analytics-free-tools";
import { hasDevanagari } from "@/lib/place-devanagari";
import {
  getBirthDetails,
  saveBirthDetails,
  type SavedBirthDetails,
} from "@/lib/last-birth-details";
import { birthFormLabels } from "./BirthDetailsForm.i18n";

export interface BirthDetailsFormProps {
  onSubmit: (data: KundaliRequest) => void | Promise<void>;
  loading: boolean;
  submitLabel?: string;
  error?: string;
  heading?: string;
  headingLevel?: "h2" | "h3";
  requireBirthTime?: boolean;
  requireResolvedPlace?: boolean;
  collectName?: boolean;
  optionalName?: boolean;
  language?: "en" | "hi";
  analyticsToolName?: CustomerToolKey;
  analyticsStep?: string;
  analyticsAttemptId?: string;
  analyticsPlacement?: string;
  showTrustStrip?: boolean;
  rememberBirthDetails?: boolean;
  /**
   * Replaces the shared "Don't know your birth time?" explainer for a tool
   * whose answer without a time differs from the default copy (the
   * /nakshatra/<slug>/ finder answers from the engine's day windows).
   */
  timeUnknownExplainer?: string;
  /**
   * Replaces the shared required-time hint ("Ascendant changes every ~2
   * hours") for a tool that never shows the Lagna (the rashi calculator: the
   * Moon can change sign within the day).
   */
  timeRequiredHint?: string;
  /**
   * Collapses that explainer when a submission goes through, inside the
   * submit event itself, and keeps its toggle inactive while `loading`, so the
   * form is back at its collapsed height before the reply lands. For a form in
   * a reserved-height slot (the /nakshatra/<slug>/ finder), where an explainer
   * left open would move the page below when the reply replaces or grows the
   * form. Off by default: every other form keeps the explainer as it was.
   */
  collapseTimeUnknownOnSubmit?: boolean;
  /**
   * Scopes the fields' DOM ids (`<field>-<scope>`, fieldId) in place of the
   * heading, for a page that holds two forms without a heading: the /hi/upay/
   * hub's chart check sits below the remedies tool, and with both on
   * "-default" ids a label or a focus hand-off reached the other form.
   * BirthToolFormShell takes the same prop.
   */
  fieldIdScope?: string;
}

type BirthFormLabels = ReturnType<typeof birthFormLabels>;

interface BirthFormState {
  name: string;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  selectedPlace: PlaceResult | null;
}

/** The fields that can carry an inline error, in the order they appear. */
type FieldKey = "name" | "birthDate" | "birthTime" | "birthPlace";
type FieldErrors = Partial<Record<FieldKey, string>>;
const FIELD_ORDER: readonly FieldKey[] = ["name", "birthDate", "birthTime", "birthPlace"];

type SubmissionResult =
  | { ok: true; request: KundaliRequest }
  | { ok: false; fieldErrors: FieldErrors; formError?: string };

type SubmissionOptions = Pick<
  BirthDetailsFormProps,
  "heading" | "requireResolvedPlace" | "requireBirthTime"
> & { collectName: boolean; optionalName: boolean; placeNoMatch: boolean };

/**
 * The DOM id of a form field: `<field>-<fieldIdScope, heading or "default">`. One source for
 * the form and its tests; BirthToolFormShell mirrors it to hand focus over.
 */
export function fieldId(name: string, heading?: string): string {
  return `${name}-${heading || "default"}`;
}

function placeResult(saved: SavedBirthDetails): PlaceResult | null {
  if (!saved.birth_place || saved.latitude == null || saved.longitude == null) return null;
  return {
    geoname_id: 0,
    name: saved.birth_place,
    display: saved.birth_place,
    latitude: saved.latitude,
    longitude: saved.longitude,
    timezone: saved.timezone ?? "",
    country: "",
    population: 0,
  };
}

const EMPTY_STATE: BirthFormState = {
  name: "",
  birthDate: "",
  birthTime: "",
  birthPlace: "",
  selectedPlace: null,
};

/**
 * The saved profile fills only fields that are still empty, so nothing the
 * seeker already typed (even before hydration finished) is overwritten.
 * @internal
 */
export function withSavedDetails(
  current: BirthFormState,
  saved: SavedBirthDetails
): BirthFormState {
  const fill = (typed: string, stored: string | undefined) => typed || stored || "";
  const placeEmpty = !current.birthPlace && !current.selectedPlace;
  return {
    name: fill(current.name, saved.name),
    birthDate: fill(current.birthDate, saved.birth_date),
    birthTime: fill(current.birthTime, saved.birth_time),
    ...(placeEmpty
      ? { birthPlace: saved.birth_place ?? "", selectedPlace: placeResult(saved) }
      : { birthPlace: current.birthPlace, selectedPlace: current.selectedPlace }),
  };
}

const subscribeToNothing = () => () => {};
const clientSnapshot = () => true;
const serverSnapshot = () => false;

/**
 * False on the server AND during the client's hydration render, true on every
 * render after it (and from the first render of a client-only mount, e.g. a
 * deferred calculator island). The lint-clean useSyncExternalStore form of
 * "has hydrated" (same as app/ai-astrologer/TopicSelect.tsx).
 */
function useHydrated(): boolean {
  return useSyncExternalStore(subscribeToNothing, clientSnapshot, serverSnapshot);
}

/**
 * C09 (t-errors-02): the server has no localStorage, so the first client
 * render must not read it. Reading `ishvaram_birth_details` in the useState
 * initializer (f2cb1a62d) swapped the place input for the chip during
 * hydration — React #418 on every return visit to an SSR'd calculator, a
 * whole-document client re-render that also reset <html lang> on /hi/ pages.
 * The profile is applied on the first render after hydration instead, as a
 * state adjustment during render (react.dev "storing information from
 * previous renders"): React re-renders before committing, so a client-only
 * mount never paints the empty form first, and no effect sets state.
 */
function useSavedBirthDetails(
  rememberBirthDetails: boolean
): [BirthFormState, (update: (current: BirthFormState) => BirthFormState) => void] {
  const hydrated = useHydrated();
  const [state, setState] = useState<BirthFormState>(EMPTY_STATE);
  const [savedApplied, setSavedApplied] = useState(false);
  if (rememberBirthDetails && hydrated && !savedApplied) {
    setSavedApplied(true);
    const saved = getBirthDetails();
    setState((current) => withSavedDetails(current, saved));
  }
  return [state, setState];
}

/**
 * Today as YYYY-MM-DD on the seeker's own calendar: the day the schema's
 * future-date rule compares with (kundaliFormSchema's local date key). The
 * picker's `max` (todayIso) stays UTC so the server and client render agree.
 */
function localTodayIso(): string {
  const now = new Date();
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The date rules the schema enforces, in the form's own words. noValidate
 * dropped the native max={today} check, so a later date this year must be
 * caught here, not left to the schema's generic form-level failure.
 */
function validateDate(value: string, labels: BirthFormLabels): string | undefined {
  if (!value) return undefined;
  const year = Number.parseInt(value.slice(0, 4), 10);
  if (Number.isNaN(year) || year < 1900) return labels.yearTooEarly;
  // ISO dates compare as strings; a 5-digit year sorts after any 4-digit one.
  if (value > localTodayIso()) return labels.futureDate;
  return undefined;
}

function effectiveName(
  state: BirthFormState,
  collectName: boolean,
  optionalName: boolean,
  heading?: string
): string {
  if (!collectName) return heading ?? "Birth details";
  return state.name.trim() || (optionalName ? (heading ?? "") : state.name);
}

/**
 * The correction path for a typed city that is not locked: "pick from the
 * suggestions" only while suggestions exist. A town the gazetteer cannot find
 * gets a way forward instead, in English spelling for a Devanagari query
 * outside the transliteration table (C20).
 */
function unresolvedPlaceError(
  birthPlace: string,
  placeNoMatch: boolean,
  labels: BirthFormLabels
): string {
  if (!placeNoMatch) return labels.pickCityToContinue;
  return hasDevanagari(birthPlace) ? labels.placeNoMatchDevanagari : labels.placeNoMatch;
}

function placeError(
  state: BirthFormState,
  labels: BirthFormLabels,
  options: SubmissionOptions
): string | undefined {
  if (state.selectedPlace) return undefined;
  if (!state.birthPlace.trim()) return labels.enterBirthPlace;
  if (options.requireResolvedPlace) {
    return unresolvedPlaceError(state.birthPlace, options.placeNoMatch, labels);
  }
  return undefined;
}

/**
 * Every field that blocks the submit, each with its own localized message
 * (c-forms-06). The form is noValidate, so these replace the browser's bubble.
 */
function requiredFieldErrors(
  state: BirthFormState,
  labels: BirthFormLabels,
  options: SubmissionOptions
): FieldErrors {
  const errors: FieldErrors = {
    name:
      options.collectName && !options.optionalName && !state.name.trim()
        ? labels.enterName
        : undefined,
    birthDate: state.birthDate ? validateDate(state.birthDate, labels) : labels.enterBirthDate,
    birthTime: options.requireBirthTime && !state.birthTime ? labels.enterBirthTime : undefined,
    birthPlace: placeError(state, labels, options),
  };
  return Object.fromEntries(
    Object.entries(errors).filter(([, message]) => message !== undefined)
  ) as FieldErrors;
}

function firstInvalidField(errors: FieldErrors): FieldKey | undefined {
  return FIELD_ORDER.find((field) => errors[field]);
}

function parseSubmission(
  state: BirthFormState,
  labels: BirthFormLabels,
  options: SubmissionOptions
): SubmissionResult {
  const fieldErrors = requiredFieldErrors(state, labels, options);
  if (firstInvalidField(fieldErrors)) return { ok: false, fieldErrors };
  const parsed = kundaliFormSchema.safeParse({
    name: effectiveName(state, options.collectName, options.optionalName, options.heading),
    birthDate: state.birthDate,
    birthTime: state.birthTime || null,
    birthPlace: state.birthPlace,
    ...(state.selectedPlace && {
      latitude: state.selectedPlace.latitude,
      longitude: state.selectedPlace.longitude,
      timezone: state.selectedPlace.timezone,
    }),
  });
  if (parsed.success) {
    return { ok: true, request: toKundaliRequest(parsed.data) as KundaliRequest };
  }
  const failed = (field: FieldKey) => parsed.error.issues.some((issue) => issue.path[0] === field);
  if (failed("birthPlace")) {
    return {
      ok: false,
      fieldErrors: {
        birthPlace: unresolvedPlaceError(state.birthPlace, options.placeNoMatch, labels),
      },
    };
  }
  // validateDate already caught the year and future-date rules; what is left
  // is a malformed or impossible date. It still belongs on the date field.
  if (failed("birthDate")) return { ok: false, fieldErrors: { birthDate: labels.invalidDate } };
  return { ok: false, fieldErrors: {}, formError: labels.checkDetails };
}

function rememberSubmission(state: BirthFormState, collectName: boolean): void {
  saveBirthDetails({
    name: collectName ? state.name.trim() : undefined,
    birth_date: state.birthDate,
    birth_time: state.birthTime || undefined,
    birth_place: state.birthPlace,
    ...(state.selectedPlace && {
      latitude: state.selectedPlace.latitude,
      longitude: state.selectedPlace.longitude,
      timezone: state.selectedPlace.timezone,
    }),
  });
}

/**
 * True when the field's top and bottom edges are the topmost thing on screen:
 * not scrolled away and not under the sticky header, strip or bottom bar.
 * Hit-testing finds whatever chrome covers it, so no header height is copied
 * here. Without hit-testing (jsdom), the field counts as in view.
 */
function inClearView(field: HTMLElement): boolean {
  if (typeof document.elementFromPoint !== "function") return true;
  const rect = field.getBoundingClientRect();
  const x = rect.left + rect.width / 2;
  return [rect.top + 2, rect.bottom - 2].every((y) => {
    const hit = document.elementFromPoint(x, y);
    return hit !== null && (hit === field || field.contains(hit));
  });
}

/**
 * Focus a field to fix. A plain focus() scrolled it only to the viewport's
 * edge, under the 100 px sticky header, label hidden (seen at 360 px); a
 * covered field goes to the middle of the screen, a visible one stays put.
 */
function focusInClearView(field: HTMLElement): void {
  field.focus({ preventScroll: true });
  if (!inClearView(field)) field.scrollIntoView?.({ block: "center" });
}

function useTrackStart(props: BirthDetailsFormProps, language: "en" | "hi"): () => void {
  const trackedStartRef = useRef(false);
  return useCallback(() => {
    if (!props.analyticsToolName || trackedStartRef.current) return;
    trackedStartRef.current = true;
    trackFreeToolStarted({
      tool_name: props.analyticsToolName,
      source_path: window.location.pathname,
      language,
      placement: props.analyticsPlacement ?? "birth_details_form",
      step: props.analyticsStep,
      ...(props.analyticsAttemptId ? { attempt_id: props.analyticsAttemptId } : {}),
    });
  }, [
    language,
    props.analyticsPlacement,
    props.analyticsStep,
    props.analyticsToolName,
    props.analyticsAttemptId,
  ]);
}

/**
 * What a blocked submit shows: inline field errors, the form-level message,
 * and a per-attempt counter. The counter keys the visible errors, so every
 * tap re-mounts (and re-announces) them, and the first invalid field takes
 * focus after the render that wired its error into aria-describedby, still
 * inside the tap's task (layout effect) so a phone raises the keyboard. Focus
 * on the city also re-opens its suggestion list, and a focus move is what the
 * dead-click reporter counts as a response: taps 2 and 3 are never dead (C20).
 */
function useBlockedSubmitFeedback(idScope: string | undefined) {
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [submitBlockedError, setSubmitBlockedError] = useState<string>();
  const [attempt, setAttempt] = useState<{ count: number; focus?: FieldKey }>({ count: 0 });

  useLayoutEffect(() => {
    if (!attempt.focus) return;
    const field = document.getElementById(fieldId(attempt.focus, idScope));
    if (field) focusInClearView(field);
  }, [attempt, idScope]);

  return {
    fieldErrors,
    submitBlockedError,
    blockedAttemptCount: attempt.count,
    block(result: Extract<SubmissionResult, { ok: false }>): void {
      setFieldErrors(result.fieldErrors);
      setSubmitBlockedError(result.formError);
      setAttempt(({ count }) => ({
        count: count + 1,
        focus: firstInvalidField(result.fieldErrors),
      }));
    },
    clear(): void {
      setFieldErrors({});
      setSubmitBlockedError(undefined);
    },
    setFieldError(field: FieldKey, message: string | undefined): void {
      setFieldErrors((current) => {
        if (current[field] === message) return current;
        const { [field]: _previous, ...rest } = current;
        return message ? { ...rest, [field]: message } : rest;
      });
    },
  };
}

/** Prop defaults the form renders with (kept out of the hook's branch count). */
function displayOptions(props: BirthDetailsFormProps, labels: BirthFormLabels) {
  const requireBirthTime = props.requireBirthTime ?? false;
  return {
    requireBirthTime,
    timeHint: requireBirthTime
      ? (props.timeRequiredHint ?? labels.timeRequiredHint)
      : labels.timeOptionalHint,
    showTrustStrip: props.showTrustStrip ?? false,
    effectiveSubmitLabel: props.submitLabel ?? labels.defaultSubmit,
  };
}

export function useBirthDetailsFormController(props: BirthDetailsFormProps) {
  const language = props.language ?? "en";
  const labels = birthFormLabels(language);
  const collectName = props.collectName ?? true;
  const optionalName = props.optionalName ?? false;
  // Default OFF: the shared store holds the SEEKER'S OWN details, and only
  // self-chart surfaces may opt in (last-birth-details.ts privacy contract).
  // The consumers that rely on this default are exactly baby-names (the
  // child) and kundli-matching Person A/B (a partner) — a
  // `?? true` default prefills AND persists the wrong person's details there.
  const rememberBirthDetails = props.rememberBirthDetails ?? false;
  const [state, setState] = useSavedBirthDetails(rememberBirthDetails);
  // Set by the seeker's own place edits, never by the saved-profile fill: a
  // saved place without coordinates must not grow the unresolved-place hint
  // after hydration (a layout shift no input excuses).
  const [placeEdited, setPlaceEdited] = useState(false);
  // Reported by PlaceAutocomplete once a 4+ character search settles empty.
  const [placeNoMatch, setPlaceNoMatch] = useState(false);
  const feedback = useBlockedSubmitFeedback(props.fieldIdScope ?? props.heading);
  const [timeUnknownOpen, setTimeUnknownOpen] = useState(false);
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const trackStart = useTrackStart(props, language);

  function setField<K extends keyof BirthFormState>(field: K, value: BirthFormState[K]): void {
    const isPlace = field === "birthPlace" || field === "selectedPlace";
    if (isPlace) setPlaceEdited(true);
    // An edit answers its field's error; a city pick (b-calc-11) must never
    // leave the city error standing beside the "✓ city" chip. The date keeps
    // its live year check (handleDateChange).
    if (field !== "birthDate")
      feedback.setFieldError(isPlace ? "birthPlace" : (field as FieldKey), undefined);
    setState((current) => ({ ...current, [field]: value }));
  }

  function handleDateChange(value: string): void {
    trackStart();
    setField("birthDate", value);
    feedback.setFieldError("birthDate", validateDate(value, labels));
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    trackStart();
    const result = parseSubmission(state, labels, {
      heading: props.heading,
      requireBirthTime: props.requireBirthTime,
      requireResolvedPlace: props.requireResolvedPlace,
      collectName,
      optionalName,
      placeNoMatch,
    });
    if (!result.ok) {
      feedback.block(result);
      return;
    }
    feedback.clear();
    if (rememberBirthDetails) rememberSubmission(state, collectName);
    // Blocked submits keep it: their error shows inside this same event.
    if (props.collapseTimeUnknownOnSubmit) setTimeUnknownOpen(false);
    void props.onSubmit(result.request);
  }

  return {
    ...state,
    labels,
    collectName,
    optionalName,
    ...displayOptions(props, labels),
    fieldErrors: feedback.fieldErrors,
    dateError: feedback.fieldErrors.birthDate,
    submitBlockedError: feedback.submitBlockedError,
    blockedAttemptCount: feedback.blockedAttemptCount,
    placeNoMatch,
    setPlaceNoMatch,
    todayIso,
    placeIsUnresolved:
      placeEdited && state.birthPlace.trim().length > 0 && state.selectedPlace === null,
    timeUnknownOpen,
    trackStart,
    setField,
    handleDateChange,
    toggleTimeUnknown: () => setTimeUnknownOpen((open) => !open),
    handleSubmit,
  };
}

export type { BirthFormLabels };
